import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  School as ScormIcon,
  PlayLesson as Cmi5Icon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface ImportRecord {
  id: string;
  importType: string;
  status: string;
  originalFilename?: string;
  sourceUrl?: string;
  errorMessage?: string;
  createdAt: string;
  processedAt?: string;
}

interface Course {
  id: string;
  title: string;
  slug: string;
}

interface Module {
  id: string;
  title: string;
  course: { title: string; slug: string };
}

interface Language {
  id: string;
  code: string;
  name: string;
  nativeName: string;
}

export default function AdminImportPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);

  // Form states
  const [scormFile, setScormFile] = useState<File | null>(null);
  const [scormCourseId, setScormCourseId] = useState('');
  const [scormLanguage, setScormLanguage] = useState('de');

  const [cmi5File, setCmi5File] = useState<File | null>(null);
  const [cmi5CourseId, setCmi5CourseId] = useState('');
  const [cmi5Language, setCmi5Language] = useState('de');

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfModuleId, setPdfModuleId] = useState('');
  const [pdfLanguage, setPdfLanguage] = useState('de');
  const [pdfDescription, setPdfDescription] = useState('');
  const [pdfDuration, setPdfDuration] = useState('');
  const [pdfPoints, setPdfPoints] = useState('10');

  const [weblinkUrl, setWeblinkUrl] = useState('');
  const [weblinkTitle, setWeblinkTitle] = useState('');
  const [weblinkModuleId, setWeblinkModuleId] = useState('');
  const [weblinkLanguage, setWeblinkLanguage] = useState('de');
  const [weblinkDescription, setWeblinkDescription] = useState('');
  const [weblinkDuration, setWeblinkDuration] = useState('');
  const [weblinkPoints, setWeblinkPoints] = useState('10');
  const [weblinkNewTab, setWeblinkNewTab] = useState(true);

  // Queries
  const { data: courses = [] } = useQuery<Course[]>({
    queryKey: ['admin', 'courses'],
    queryFn: async () => {
      const response = await api.get('/admin/courses');
      return response.data;
    },
  });

  const { data: modules = [] } = useQuery<Module[]>({
    queryKey: ['admin', 'modules'],
    queryFn: async () => {
      const response = await api.get('/admin/modules');
      return response.data;
    },
  });

  const { data: languages = [] } = useQuery<Language[]>({
    queryKey: ['languages'],
    queryFn: async () => {
      const response = await api.get('/system/languages');
      return response.data;
    },
  });

  const { data: imports = [], isLoading: importsLoading, refetch: refetchImports } = useQuery<ImportRecord[]>({
    queryKey: ['admin', 'imports'],
    queryFn: async () => {
      const response = await api.get('/admin/imports');
      return response.data;
    },
  });

  // Mutations
  const scormImportMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/admin/import/scorm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        enqueueSnackbar(t('common.success'), { variant: 'success' });
        setScormFile(null);
        setScormCourseId('');
        queryClient.invalidateQueries({ queryKey: ['admin', 'imports'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] });
      } else {
        enqueueSnackbar(data.error || t('errors.generic'), { variant: 'error' });
      }
    },
    onError: () => {
      enqueueSnackbar(t('errors.generic'), { variant: 'error' });
    },
  });

  const cmi5ImportMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/admin/import/cmi5', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        enqueueSnackbar(t('common.success'), { variant: 'success' });
        setCmi5File(null);
        setCmi5CourseId('');
        queryClient.invalidateQueries({ queryKey: ['admin', 'imports'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'courses'] });
      } else {
        enqueueSnackbar(data.error || t('errors.generic'), { variant: 'error' });
      }
    },
    onError: () => {
      enqueueSnackbar(t('errors.generic'), { variant: 'error' });
    },
  });

  const pdfImportMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/admin/import/pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        enqueueSnackbar(t('common.success'), { variant: 'success' });
        setPdfFile(null);
        setPdfTitle('');
        setPdfDescription('');
        setPdfDuration('');
        queryClient.invalidateQueries({ queryKey: ['admin', 'imports'] });
      } else {
        enqueueSnackbar(data.error || t('errors.generic'), { variant: 'error' });
      }
    },
    onError: () => {
      enqueueSnackbar(t('errors.generic'), { variant: 'error' });
    },
  });

  const weblinkImportMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/admin/import/weblink', data);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        enqueueSnackbar(t('common.success'), { variant: 'success' });
        setWeblinkUrl('');
        setWeblinkTitle('');
        setWeblinkDescription('');
        setWeblinkDuration('');
        queryClient.invalidateQueries({ queryKey: ['admin', 'imports'] });
      } else {
        enqueueSnackbar(data.error || t('errors.generic'), { variant: 'error' });
      }
    },
    onError: () => {
      enqueueSnackbar(t('errors.generic'), { variant: 'error' });
    },
  });

  // Handlers
  const handleScormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!scormFile) return;

    const formData = new FormData();
    formData.append('file', scormFile);
    formData.append('languageCode', scormLanguage);
    if (scormCourseId) formData.append('targetCourseId', scormCourseId);

    scormImportMutation.mutate(formData);
  };

  const handleCmi5Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cmi5File) return;

    const formData = new FormData();
    formData.append('file', cmi5File);
    formData.append('languageCode', cmi5Language);
    if (cmi5CourseId) formData.append('targetCourseId', cmi5CourseId);

    cmi5ImportMutation.mutate(formData);
  };

  const handlePdfSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pdfFile || !pdfTitle || !pdfModuleId) return;

    const formData = new FormData();
    formData.append('file', pdfFile);
    formData.append('title', pdfTitle);
    formData.append('targetModuleId', pdfModuleId);
    formData.append('languageCode', pdfLanguage);
    if (pdfDescription) formData.append('description', pdfDescription);
    if (pdfDuration) formData.append('estimatedDurationMinutes', pdfDuration);
    if (pdfPoints) formData.append('pointsReward', pdfPoints);

    pdfImportMutation.mutate(formData);
  };

  const handleWeblinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!weblinkUrl || !weblinkTitle || !weblinkModuleId) return;

    weblinkImportMutation.mutate({
      url: weblinkUrl,
      title: weblinkTitle,
      targetModuleId: weblinkModuleId,
      languageCode: weblinkLanguage,
      description: weblinkDescription || undefined,
      estimatedDurationMinutes: weblinkDuration ? parseInt(weblinkDuration) : undefined,
      pointsReward: weblinkPoints ? parseInt(weblinkPoints) : undefined,
      openInNewTab: weblinkNewTab,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('admin.import.title')}
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<ScormIcon />} label={t('admin.import.scorm')} />
          <Tab icon={<Cmi5Icon />} label="cmi5" />
          <Tab icon={<PdfIcon />} label={t('admin.import.pdf')} />
          <Tab icon={<LinkIcon />} label={t('admin.import.weblink')} />
          <Tab icon={<HistoryIcon />} label={t('admin.import.importHistory')} />
        </Tabs>

        {/* SCORM Import */}
        <TabPanel value={tabValue} index={0}>
          <form onSubmit={handleScormSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('admin.import.uploadFile')}
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                      sx={{ py: 2 }}
                    >
                      {scormFile ? scormFile.name : t('admin.import.scorm') + ' (ZIP)'}
                      <input
                        type="file"
                        hidden
                        accept=".zip"
                        onChange={(e) => setScormFile(e.target.files?.[0] || null)}
                      />
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('admin.import.selectCourse')}</InputLabel>
                  <Select
                    value={scormCourseId}
                    label={t('admin.import.selectCourse')}
                    onChange={(e) => setScormCourseId(e.target.value)}
                  >
                    <MenuItem value="">{t('common.create')} - Neuer Kurs</MenuItem>
                    {courses.map((course) => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('admin.import.selectLanguage')}</InputLabel>
                  <Select
                    value={scormLanguage}
                    label={t('admin.import.selectLanguage')}
                    onChange={(e) => setScormLanguage(e.target.value)}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!scormFile || scormImportMutation.isPending}
                  startIcon={scormImportMutation.isPending ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {t('common.upload')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        {/* cmi5 Import */}
        <TabPanel value={tabValue} index={1}>
          <Alert severity="info" sx={{ mb: 3 }}>
            cmi5 ist ein moderner xAPI-basierter E-Learning-Standard. Das Paket muss eine <strong>cmi5.xml</strong> Manifest-Datei enthalten.
          </Alert>
          <form onSubmit={handleCmi5Submit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('admin.import.uploadFile')}
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                      sx={{ py: 2 }}
                    >
                      {cmi5File ? cmi5File.name : 'cmi5 Paket (ZIP)'}
                      <input
                        type="file"
                        hidden
                        accept=".zip"
                        onChange={(e) => setCmi5File(e.target.files?.[0] || null)}
                      />
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('admin.import.selectCourse')}</InputLabel>
                  <Select
                    value={cmi5CourseId}
                    label={t('admin.import.selectCourse')}
                    onChange={(e) => setCmi5CourseId(e.target.value)}
                  >
                    <MenuItem value="">{t('common.create')} - Neuer Kurs</MenuItem>
                    {courses.map((course) => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('admin.import.selectLanguage')}</InputLabel>
                  <Select
                    value={cmi5Language}
                    label={t('admin.import.selectLanguage')}
                    onChange={(e) => setCmi5Language(e.target.value)}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!cmi5File || cmi5ImportMutation.isPending}
                  startIcon={cmi5ImportMutation.isPending ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {t('common.upload')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        {/* PDF Import */}
        <TabPanel value={tabValue} index={2}>
          <form onSubmit={handlePdfSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('admin.import.uploadFile')}
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      fullWidth
                      sx={{ py: 2 }}
                    >
                      {pdfFile ? pdfFile.name : t('admin.import.pdf') + ' (PDF)'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf"
                        onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                      />
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('admin.import.importTitle')}
                  value={pdfTitle}
                  onChange={(e) => setPdfTitle(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('admin.import.selectModule')}</InputLabel>
                  <Select
                    value={pdfModuleId}
                    label={t('admin.import.selectModule')}
                    onChange={(e) => setPdfModuleId(e.target.value)}
                  >
                    {modules.map((mod) => (
                      <MenuItem key={mod.id} value={mod.id}>
                        {mod.course.title} / {mod.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t('admin.import.selectLanguage')}</InputLabel>
                  <Select
                    value={pdfLanguage}
                    label={t('admin.import.selectLanguage')}
                    onChange={(e) => setPdfLanguage(e.target.value)}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('admin.import.duration')}
                  type="number"
                  value={pdfDuration}
                  onChange={(e) => setPdfDuration(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('admin.import.points')}
                  type="number"
                  value={pdfPoints}
                  onChange={(e) => setPdfPoints(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('admin.import.description')}
                  value={pdfDescription}
                  onChange={(e) => setPdfDescription(e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!pdfFile || !pdfTitle || !pdfModuleId || pdfImportMutation.isPending}
                  startIcon={pdfImportMutation.isPending ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {t('common.upload')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        {/* Weblink Import */}
        <TabPanel value={tabValue} index={3}>
          <form onSubmit={handleWeblinkSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('admin.import.url')}
                  value={weblinkUrl}
                  onChange={(e) => setWeblinkUrl(e.target.value)}
                  placeholder="https://"
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('admin.import.importTitle')}
                  value={weblinkTitle}
                  onChange={(e) => setWeblinkTitle(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('admin.import.selectModule')}</InputLabel>
                  <Select
                    value={weblinkModuleId}
                    label={t('admin.import.selectModule')}
                    onChange={(e) => setWeblinkModuleId(e.target.value)}
                  >
                    {modules.map((mod) => (
                      <MenuItem key={mod.id} value={mod.id}>
                        {mod.course.title} / {mod.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t('admin.import.selectLanguage')}</InputLabel>
                  <Select
                    value={weblinkLanguage}
                    label={t('admin.import.selectLanguage')}
                    onChange={(e) => setWeblinkLanguage(e.target.value)}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('admin.import.duration')}
                  type="number"
                  value={weblinkDuration}
                  onChange={(e) => setWeblinkDuration(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('admin.import.points')}
                  type="number"
                  value={weblinkPoints}
                  onChange={(e) => setWeblinkPoints(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('admin.import.description')}
                  value={weblinkDescription}
                  onChange={(e) => setWeblinkDescription(e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={weblinkNewTab}
                      onChange={(e) => setWeblinkNewTab(e.target.checked)}
                    />
                  }
                  label={t('admin.import.openInNewTab')}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!weblinkUrl || !weblinkTitle || !weblinkModuleId || weblinkImportMutation.isPending}
                  startIcon={weblinkImportMutation.isPending ? <CircularProgress size={20} /> : <LinkIcon />}
                >
                  {t('common.submit')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>

        {/* Import History */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button startIcon={<RefreshIcon />} onClick={() => refetchImports()}>
              {t('common.loading').replace('...', '')}
            </Button>
          </Box>

          {importsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : imports.length === 0 ? (
            <Alert severity="info">{t('common.noData')}</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Typ</TableCell>
                    <TableCell>Datei/URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell>Verarbeitet</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {imports.map((imp) => (
                    <TableRow key={imp.id}>
                      <TableCell>
                        <Chip
                          size="small"
                          label={imp.importType.toUpperCase()}
                          color={
                            imp.importType === 'scorm'
                              ? 'primary'
                              : imp.importType === 'cmi5'
                              ? 'info'
                              : imp.importType === 'pdf'
                              ? 'secondary'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {imp.originalFilename || imp.sourceUrl || '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(`admin.import.status.${imp.status}`)}
                          color={getStatusColor(imp.status) as any}
                        />
                        {imp.errorMessage && (
                          <Typography variant="caption" color="error" display="block">
                            {imp.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(imp.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {imp.processedAt
                          ? new Date(imp.processedAt).toLocaleString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
}
